import { useCallback, useEffect, useRef, useState } from "react";
import type { ReportReason, ReportTarget } from "@givehere/contracts";
import { DonorApiError, sendContentReport } from "../donor-session.js";
import { reportDeviceId } from "./hiddenStore.js";
import { ReportDialog } from "./ReportDialog.js";

export const REPORT_RATE_LIMITED =
  "Too many reports right now. Please try again in a little while.";
export const REPORT_FAILED = "We could not send your report. Check your connection and try again.";

type Outcome = { readonly done: true } | { readonly done: false; readonly message: string };

const outcomeOf = (caught: unknown): Outcome => {
  if (caught instanceof DonorApiError) {
    // The item is gone or no longer showing: nothing is left to report, so the wish is met.
    if (caught.status === 404 || caught.code === "not_found") return { done: true };
    if (caught.status === 429 || caught.code === "rate_limited") {
      return { done: false, message: REPORT_RATE_LIMITED };
    }
  }
  return { done: false, message: REPORT_FAILED };
};

/**
 * The "⋯" on one item of someone else's words, its one-item menu, and the Report dialog.
 * `itemKey` is what hide-for-me remembers: the submission id, or a gift's `reportRef`.
 */
export function ReportMenu({
  eventId,
  target,
  itemKey,
  onReported,
}: {
  readonly eventId: string;
  readonly target: ReportTarget;
  readonly itemKey: string;
  readonly onReported: (key: string) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const onPointer = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [menuOpen]);

  const cancel = useCallback(() => {
    setDialogOpen(false);
    setError(null);
  }, []);

  const send = (reason: ReportReason, rawNote: string) => {
    const note = rawNote.trim();
    setBusy(true);
    setError(null);
    sendContentReport(eventId, {
      target,
      reason,
      ...(note === "" ? {} : { note }),
      deviceId: reportDeviceId(),
    })
      .then((): Outcome => ({ done: true }))
      .catch(outcomeOf)
      .then((outcome) => {
        setBusy(false);
        if (outcome.done) {
          setDialogOpen(false);
          onReported(itemKey);
        } else {
          setError(outcome.message);
        }
      });
  };

  return (
    <span className="report" ref={wrapRef}>
      <button
        type="button"
        className="report__more"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {menuOpen && (
        <div className="report__menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="report__item"
            onClick={() => {
              setMenuOpen(false);
              setDialogOpen(true);
            }}
          >
            Report
          </button>
        </div>
      )}
      {dialogOpen && <ReportDialog busy={busy} error={error} onCancel={cancel} onSend={send} />}
    </span>
  );
}
